import { useCallback } from 'react'

import { useMutation } from '@apollo/client/react'

import { useCurrentOrganization } from '~publish/contexts/AccountContext'
import { graphql } from '~publish/graphql'
import type { ResultOf } from '~publish/graphql'

import { SavedRepliesQuery } from './useSavedRepliesQuery'
import type { SavedReplyId } from '../../types'

type SavedRepliesQueryQuery = ResultOf<typeof SavedRepliesQuery>

export type DeleteSavedReply = (params: {
  id: SavedReplyId
}) => Promise<SavedReplyId>

export const DeleteSavedReply = graphql(/* GraphQL */ `
  mutation DeleteSavedReply($input: DeleteSavedReplyInput!) {
    deleteSavedReply(input: $input) {
      ... on DeleteSavedReplySuccess {
        id
      }
      ... on MutationError {
        __typename
        message
      }
    }
  }
`)

/**
 * Hook to delete a saved reply.
 *
 * @returns {DeleteSavedReply} Function to delete a saved reply.
 * @example
 * const deleteSavedReply = useDeleteSavedReply();
 *
 * const handleDeleteSavedReply = async () => {
 *   try {
 *     const deletedSavedReplyId = await deleteSavedReply({ id: '123' });
 *   } catch (err) {
 *     console.error(err);
 *   }
 * };
 */
export function useDeleteSavedReply(): DeleteSavedReply {
  const organizationId = useCurrentOrganization()?.id ?? ''

  const [deleteSavedReplyMutation] = useMutation(DeleteSavedReply, {
    update(cache, { data }) {
      if (
        !data?.deleteSavedReply ||
        data.deleteSavedReply.__typename !== 'DeleteSavedReplySuccess'
      )
        return

      const deletedId = data.deleteSavedReply.id

      const variables = {
        input: { organizationId },
      }

      // Read from the cache with correct variables
      const existingData = cache.readQuery<SavedRepliesQueryQuery>({
        query: SavedRepliesQuery,
        variables,
      })

      if (!existingData) return

      // Filter out the deleted item
      const newItems = existingData.savedReplies.savedReplies.filter(
        (item) => item.id !== deletedId,
      )

      // `__typename` is required by Apollo at write time but isn't part of the
      // query's selection set (so it's absent from the derived type). Building
      // the payload as a separate object keeps it out of the excess-property
      // check while preserving the runtime shape Apollo needs.
      const updatedData = {
        savedReplies: {
          __typename: 'SavedRepliesResponse',
          savedReplies: newItems,
        },
      }

      cache.writeQuery({
        query: SavedRepliesQuery,
        variables,
        data: updatedData,
      })
    },
  })

  const deleteSavedReply = useCallback(
    async ({ id }: { id: string }): Promise<SavedReplyId> => {
      if (!id) {
        throw new Error('Saved Reply Id is required')
      }

      const { data, error } = await deleteSavedReplyMutation({
        variables: {
          input: {
            id,
          },
        },
      })

      if (error || !data) {
        throw new Error(error?.message ?? 'Failed to delete saved reply')
      }

      const result = data.deleteSavedReply

      if (result.__typename !== 'DeleteSavedReplySuccess') {
        throw new Error(
          'message' in result && typeof result.message === 'string'
            ? result.message
            : 'Failed to delete saved reply',
        )
      }

      return result.id
    },
    [deleteSavedReplyMutation],
  )

  return deleteSavedReply
}
